import React, {
  useEffect,
} from 'react';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import EditIcon from 'core/icons/edit';
import Skeleton from 'core/components/Skeleton';
import {
  PatientMedicalBackground as PatientMedicalData,
  useGetPatientMedicalBackground,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  VisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  patientEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  patientMedicalBackgroundSections,
} from 'views/EMR/components/MedicalBackgroundDialog';
import uniqueId from 'lodash/uniqueId';

type Part = 'root' | 'title' | 'content';
type Props = Pick<VisitNote, 'patient' | 'isFinalized'>;
type SectionProps = {
  title: string;
  onEdit?: () => void;
  value?: string[];
  isLoading?: boolean;
  readonly?: boolean;
  classes?: Partial<Record<Part, string>>;
};

function List({ value }: Pick<SectionProps, 'value'>) {
  return (
    <ul className="list-disc list-inside gap-1 !break-words">
      {value?.map((item) => (
        <li key={uniqueId(item)}>{item}</li>
      ))}
    </ul>
  );
}

function Title({ title, onEdit, readonly, classes }: Partial<SectionProps>) {
  return (
    <h4 className={clsx('font-semibold text-xs', classes?.title)}>
      {title}
      {!readonly && (
        <ToolTip title={`Edit ${title}`}>
          <IconButton
            size="small"
            onClick={onEdit}
            color="info"
          >
            <EditIcon className="!w-5 !h-5 !text-inherit" />
          </IconButton>
        </ToolTip>
      )}
    </h4>
  );
}

function Section({
  title,
  value,
  onEdit,
  isLoading = false,
  readonly = false,
  classes,
}: SectionProps) {
  return (
    <div className={clsx('text-xs w-[calc(50%-8px)] flex flex-col gap-1', classes?.root)}>
      <Title
        title={title}
        readonly={readonly || isLoading}
        onEdit={onEdit}
        classes={classes}
      />
      {isLoading
        ? <Skeleton className="h-6 !w-full" /> : <List value={value} />}
    </div>
  );
}

export default function PatientMedicalBackground({
  patient,
  isFinalized = false,
}: Props) {
  const {
    data,
    isFetching,
    isFetched,
    refetch,
  } = useGetPatientMedicalBackground(patient?.patientId ?? 0);
  const handleEdit = (id: string) => {
    patientEventChannel.emit('onEditMedicalBackground', { patient, id });
  };

  useEffect(() => {
    const unsubscribeFromEditMedicalBackground = patientEventChannel.on('onMedicalBackgroundChange', (patientId) => {
      if (patientId === patient?.patientId) {
        refetch?.();
      }
    });

    return () => {
      unsubscribeFromEditMedicalBackground();
    };
  }, []);

  const ReviewOfSystemTitleComponent = data?.reviewOfSystem.length === 0 || isFetching
    ? Section : Title;

  const sections = patientMedicalBackgroundSections.slice(0, 8);

  return (
    <div className="w-full flex flex-wrap justify-start gap-2 items-start">
      {sections.map((section) => (
        <Section
          key={section.id}
          title={section.title}
          value={data?.[section?.id as keyof PatientMedicalData] as string[]}
          readonly={isFinalized}
          onEdit={() => { handleEdit(section.id); }}
          isLoading={!isFetched && isFetching}
        />
      ))}
      <ReviewOfSystemTitleComponent
        title="Review of Systems"
        isLoading={!isFetched && isFetching}
        readonly={isFinalized}
        onEdit={() => { handleEdit('groupedReviewOfSystemElements'); }}
        value={['None']}
        classes={{ root: '!w-full', title: '!w-full' }}
      />
      {data?.reviewOfSystem.map((item) => (
        <Section
          key={`review-of-system-${item.discriminator}`}
          title={item.title ?? '--'}
          value={item.values}
          classes={{ title: '!font-medium' }}
          readonly
        />
      ))}
    </div>
  );
}
